'use client';

import FormLabel from '@c/forms/controls/FormLabel';
import PageSection from '@c/listing/shared/PageSection';
import AddPaymentMethodModal from '@c/modals/AddPaymentMethodModal';
import { PlusCircleIcon } from '@heroicons/react/20/solid';
import { useQuery } from '@tanstack/react-query';
import Select from '@ui/Select';
import { getPaymentMethods } from '@util/firestore/payments';
import { capitalize, formatCurrency, isMobile } from '@util/index';
import { useCreateListing } from 'app/create-listing/_context';
import { Bookmark, Mail, Star, TrendingUp } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import { MotoIcon } from './assets/icons/MotoIcon';

// Single source of truth for package pricing — also consumed by the charge
// logic in `_context` so the displayed price and the amount we charge can't
// drift apart.
export const BIKE_PACKAGE_PRICES: Record<number, number> = {
  1: 0,
  2: 49.99,
  3: 99.99,
};

type PackageItem = { icon: ReactNode; text: ReactNode };
type Package = {
  index: 1 | 2 | 3;
  gear: string;
  items: PackageItem[];
};

const ICON_CLASS = 'h-[3rem] w-[3rem] flex-shrink-0 text-brand-secondary';

const PACKAGES: Package[] = [
  {
    index: 1,
    gear: '1st Gear',
    items: [
      {
        icon: <MotoIcon className={ICON_CLASS} />,
        text: 'List your vehicle for free',
      },
    ],
  },
  {
    index: 2,
    gear: '2nd Gear',
    items: [
      {
        icon: <Bookmark className={ICON_CLASS} />,
        text: 'Featured Badge on your listing',
      },
      {
        icon: <Star className={ICON_CLASS} />,
        text: 'Highlighted in Featured Listings',
      },
      {
        icon: <TrendingUp className={ICON_CLASS} />,
        text: (
          <>
            Premium placement for <strong className="font-bold">1 Week</strong>
          </>
        ),
      },
    ],
  },
  {
    index: 3,
    gear: '3rd Gear',
    items: [
      {
        icon: <Bookmark className={ICON_CLASS} />,
        text: 'Featured Badge on your listing',
      },
      {
        icon: <Star className={ICON_CLASS} />,
        text: 'Highlighted in Featured Listings',
      },
      {
        icon: <TrendingUp className={ICON_CLASS} />,
        text: (
          <>
            Premium placement for <strong className="font-bold">2 Weeks</strong>
          </>
        ),
      },
      {
        icon: <Mail className={ICON_CLASS} />,
        text: 'Featured in our weekly email',
      },
    ],
  },
];

export default function BikePackage() {
  const [openModal, setOpenModal] = useState('');
  const { form, selectedPaymentMethodId, setSelectedPaymentMethodId, product } =
    useCreateListing();

  const selectedIndex = product.bike_package ?? 0;
  const packagePrice = BIKE_PACKAGE_PRICES[selectedIndex] ?? 0;

  const handleSelect = (index: 1 | 2 | 3) => {
    form.setValue('bike_package', index);
  };

  const { data: paymentMethodData } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
  });
  const paymentOptions =
    paymentMethodData?.map((paymentMethod) => ({
      id: paymentMethod.id ?? '',
      label: `${capitalize(paymentMethod.brand)} - ${paymentMethod.last4}`,
      value: paymentMethod.id ?? '',
      name: paymentMethod.name,
    })) ?? [];

  // Stripe's payment-methods list doesn't expose the customer's default, so we
  // follow the project convention (MakeAnOfferForm / PlaceABidForm): pre-select
  // the first method once the list loads, unless the seller already picked one.
  useEffect(() => {
    if (!selectedPaymentMethodId && paymentOptions[0]?.value) {
      setSelectedPaymentMethodId(paymentOptions[0].value);
    }
  }, [selectedPaymentMethodId, paymentOptions, setSelectedPaymentMethodId]);

  return (
    <PageSection title="Choose your package">
      <div className="mt-8 flex flex-col items-center gap-[4rem]">
        <div className="flex w-full flex-col gap-[3rem] sm:flex-row">
          {PACKAGES.map((pkg) => (
            <PackageCard
              key={pkg.index}
              pkg={pkg}
              isSelected={selectedIndex === pkg.index}
              onSelect={() => handleSelect(pkg.index)}
            />
          ))}
        </div>

        <p className="text-[2rem] font-medium italic text-[#444]">
          *One-time fee per listing
        </p>
      </div>

      {packagePrice > 0 && (
        <div className="mt-16 w-full">
          <div className="mt-8 flex w-full flex-col items-start justify-between gap-y-16 sm:flex-row sm:items-center sm:gap-x-32 sm:gap-y-0">
            <div className="w-full sm:w-[70rem]">
              <h1 className="text-[2.4rem] font-medium text-black">
                Buy this package
              </h1>

              <FormLabel
                value="Select payment"
                required
                className="mt-8 w-full !text-[1.8rem] !font-medium !text-zinc-700"
              >
                <Select
                  allowEmpty={false}
                  options={paymentOptions}
                  value={selectedPaymentMethodId ?? ''}
                  onChange={(e) => {
                    setSelectedPaymentMethodId(e.value);
                  }}
                />
              </FormLabel>

              <div className="mt-8">
                <button
                  className="flex items-center gap-x-4 border-none bg-inherit"
                  type="button"
                  onClick={() => setOpenModal('payment')}
                >
                  <PlusCircleIcon className="h-10 w-10 text-brand-secondary" />
                  <span className="text-[1.8rem] text-brand-secondary">
                    Add new payment
                  </span>
                  <AddPaymentMethodModal
                    isOpen={openModal === 'payment'}
                    dismiss={(paymentMethodId?: string) => {
                      setOpenModal('');
                      if (paymentMethodId) {
                        setSelectedPaymentMethodId(paymentMethodId);
                      }
                    }}
                    isFullScreen={isMobile()}
                  />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-y-4 whitespace-nowrap">
              <h3 className="text-[1.8rem] font-medium text-zinc-700">
                Total purchase
              </h3>

              <h3 className="text-[2.4rem] font-semibold text-black">
                {formatCurrency(packagePrice)}
              </h3>
            </div>
          </div>
        </div>
      )}
    </PageSection>
  );
}

const PackageCard = ({
  pkg,
  isSelected,
  onSelect,
}: {
  pkg: Package;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const price = BIKE_PACKAGE_PRICES[pkg.index];
  const priceLabel = price === 0 ? 'Free' : formatCurrency(price);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`flex flex-1 cursor-pointer flex-col gap-[5rem] self-stretch rounded-[2rem] border-2 bg-zinc-100 p-[5rem] transition-colors ${
        isSelected
          ? 'border-brand-secondary'
          : 'border-transparent hover:border-brand-secondary'
      }`}
    >
      <div className="flex w-full flex-col items-start gap-[0.5rem]">
        <p className="w-full text-[3.75rem] font-bold leading-[1.3] tracking-[-0.0375rem] text-black">
          {priceLabel}
        </p>
        <p className="w-full text-[2.25rem] font-bold leading-[1.3] tracking-[-0.0225rem] text-brand-secondary">
          {pkg.gear}
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-[2rem]">
        {pkg.items.map((item, i) => (
          <div key={i} className="flex w-full items-center gap-[2rem]">
            {item.icon}
            <p className="flex-1 text-[1.75rem] font-medium leading-[1.5] text-[#444]">
              {item.text}
            </p>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className="flex h-[5.5rem] w-full items-center justify-center rounded-full bg-brand-secondary px-[2.5rem] text-[1.75rem] font-semibold text-white transition-colors hover:bg-brand-secondary-darker"
      >
        {isSelected ? 'Selected' : 'Select package'}
      </button>
    </div>
  );
};
